import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Link,
  Grid,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import { useCookies } from "react-cookie";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { config } from "../../../../config/config";

const ProfileDetailPage = () => {
  const { id } = useParams(); // Get person ID from URL params
  const navigate = useNavigate();
  const [cookies] = useCookies();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============ Fetch Individual Person Data ============
  useEffect(() => {
    const fetchPerson = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          setError("No person ID provided");
          setLoading(false);
          return;
        }

        const response = await invokeApi(
          config.getMyCollege + apiList.getPerson,
          { id: id },
          cookies
        );

        console.log("Person API Response:", response);

        if (response?.status === 200) {
          const personData = response.data.person || response.data;

          if (personData) {
            setMember({
              id: personData.id || id,
              name: personData.name || "No Name",
              position: personData.position || "",
              email: personData.email || "",
              qualifications: personData.qualifications || [],
              description: personData.description || "",
              fullProfile1: personData.fullProfile1 || "",
              fullProfile2: personData.fullProfile2 || "",
              fullProfile3: personData.fullProfile3 || "",
              imageUrl:
                personData.imageUrl ||
                personData.featuredUrl ||
                "/default-profile.jpg",
              category: personData.category || "",
              categoryUrl: personData.categoryUrl || "",
              ogUrl: personData.ogUrl || "",
              createdBy: personData.createdBy || "",
            });
          } else {
            setError("Person not found");
          }
        } else {
          setError(`Failed to fetch person: ${response?.status}`);
          console.error("Failed to fetch person response:", response);
        }
      } catch (error) {
        console.error("Person API error:", error);
        setError("Failed to load person data");
      } finally {
        setLoading(false);
      }
    };

    fetchPerson();
    window.scrollTo(0, 0);
  }, [id, cookies]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" gutterBottom color="error">
          {error || "No member data available."}
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Grid
      container
      direction="column"
      sx={{
        bgcolor: "#f5f5f5",
        color: "#fff",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <Grid item sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            color: "#000",
            borderColor: "#000",
            "&:hover": {
              borderColor: "#000",
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          ← Back
        </Button>
      </Grid>

      <Grid
        item
        container
        sx={{
          flex: 1,
          overflow: "hidden",
        }}
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Left image panel */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: { xs: "center", md: "flex-start" },
            p: 2,
          }}
          component={motion.div}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            component="img"
            src={member.imageUrl}
            alt={member.name}
            sx={{
              width: { xs: "100%", sm: "80%", md: "100%" },
              maxWidth: 320,
              height: { xs: "auto", md: 400 },
              borderRadius: 3,
              objectFit: "cover",
              boxShadow: "0 4px 15px rgba(0,0,0,0.7)",
            }}
            onError={(e) => {
              e.target.src = "/default-profile.jpg";
            }}
          />
        </Grid>

        {/* Right scrollable content */}
        <Grid
          item
          xs={12}
          md={8}
          sx={{
            height: { md: "100%", xs: "auto" },
            overflowY: { md: "auto", xs: "visible" },
            pr: { md: 2 },
            p: { md: 1, xs: 2 },
            py: 2,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#3f51b5",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }}
          component={motion.div}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontFamily: "Montserrat, sans-serif",
              color: "#000",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            {member.name}
          </Typography>

          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontFamily: "Montserrat, sans-serif",
              color: "#000",
              mb: 2,
            }}
          >
            {member.position}
          </Typography>

          {member.email && member.email !== "-" && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                fontFamily: "Montserrat, sans-serif",
                color: "#000",
              }}
            >
              Email:{" "}
              <Link
                href={`mailto:${member.email}`}
                color="secondary"
                underline="hover"
              >
                {member.email}
              </Link>
            </Typography>
          )}

          {member.qualifications && member.qualifications.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                sx={{
                  fontFamily: "Montserrat, sans-serif",
                  color: "#000",
                  mt: 3,
                }}
              >
                Qualifications:
              </Typography>
              <List dense sx={{ mb: 3 }}>
                {Array.isArray(member.qualifications) ? (
                  member.qualifications.map(
                    (qual, idx) =>
                      qual && (
                        <ListItem key={idx} sx={{ py: 0.3 }}>
                          <ListItemText
                            primary={qual}
                            primaryTypographyProps={{
                              fontFamily: "Montserrat, sans-serif",
                              color: "#000",
                            }}
                          />
                        </ListItem>
                      )
                  )
                ) : (
                  <ListItem sx={{ py: 0.3 }}>
                    <ListItemText
                      primary={member.qualifications}
                      primaryTypographyProps={{
                        fontFamily: "Montserrat, sans-serif",
                        color: "#000",
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}

          {(member.fullProfile1 ||
            member.fullProfile2 ||
            member.fullProfile3) && (
              <Typography
                variant="h6"
                sx={{
                  mt: 3,
                  mb: 1,
                  fontWeight: "bold",
                  borderLeft: "4px solid #3f51b5",
                  textAlign: "justify",
                  pl: 2,
                  fontFamily: "Montserrat, sans-serif",
                  color: "#90caf9",
                }}
              >
                Profile:
              </Typography>
            )}

          <Box sx={{ mb: 3 }}>
            {member.fullProfile1 && (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                  color: "#000",
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                  fontFamily: "Montserrat, sans-serif",
                  userSelect: "text",
                  textAlign: "justify",
                  p: 1,
                  backgroundColor: "rgba(63, 81, 181, 0.1)",
                  borderRadius: 1,
                }}
              >
                {member.fullProfile1}
              </Typography>
            )}

            {member.fullProfile2 && (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "justify",
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                  color: "#000",
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                  fontFamily: "Montserrat, sans-serif",
                  userSelect: "text",
                  p: 1,
                  backgroundColor: "rgba(63, 81, 181, 0.1)",
                  borderRadius: 1,
                }}
              >
                {member.fullProfile2}
              </Typography>
            )}

            {member.fullProfile3 && (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                  textAlign: "justify",
                  color: "#000",
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                  fontFamily: "Montserrat, sans-serif",
                  userSelect: "text",
                  p: 1,
                  backgroundColor: "rgba(63, 81, 181, 0.1)",
                  borderRadius: 1,
                }}
              >
                {member.fullProfile3}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProfileDetailPage;
